
import { Home, Search, Camera, User, TrendingUp, Settings, Handshake, Database, Shield } from "lucide-react";
import { useAnalytics } from '@/hooks/useAnalytics';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { useLiteMode } from '@/hooks/useLiteMode';
import { isTabEnabled } from '@/config/featureFlags';
import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthModal } from './AuthModal';
import { useUserRole } from '@/hooks/useUserRole';

interface NavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const Navigation = ({ activeTab, onTabChange }: NavigationProps) => {
  const { logAction } = useAnalytics();
  const { triggerLight } = useHapticFeedback();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { isLiteMode } = useLiteMode();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navRef = useRef<HTMLElement | null>(null);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authFeature, setAuthFeature] = useState<string>('');

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const allTabs = [
    { id: "home", icon: Home, label: "Home" },
    { id: "discover", icon: Search, label: "Discover" },
    { id: "trends", icon: TrendingUp, label: "Trends" },
    { id: "tag-teams", icon: Handshake, label: "tAG" },
    { id: "admin", icon: Settings, label: "Admin" },
    { id: "usda-integration", icon: Database, label: "USDA" },
    { id: "create", icon: Camera, label: "Create" },
    { id: "guidelines", icon: Shield, label: "Rules" },
    { id: "profile", icon: User, label: "Profile" },
  ];

  // Filter tabs based on feature flags and admin permissions
  const tabs = allTabs.filter(tab => {
    // Admin tab is visible to admin users or while roles are loading (to prevent disappearing UI)
    if (tab.id === 'admin') {
      return isTabEnabled(tab.id) && (user && (isAdmin || roleLoading));
    }
    return isTabEnabled(tab.id);
  });

  useEffect(() => {
    const activeIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (activeIndex !== -1) {
      setFocusedIndex(activeIndex);
    }
  }, [activeTab, tabs]);

  // Measure footer height and write to CSS variable
  useEffect(() => {
    if (!navRef.current) return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--ys-footer-h', `${navRef.current!.offsetHeight}px`);
    });
    ro.observe(navRef.current);
    return () => ro.disconnect();
  }, []);

  const handleTabClick = async (tabId: string, index: number) => {
    await triggerLight();
    
    // Check if tab requires authentication
    const authRequiredTabs = ['create', 'tag-teams', 'profile'];
    if (authRequiredTabs.includes(tabId) && !user) {
      const featureNames = {
        'create': 'Create',
        'tag-teams': 'tAG Teams',
        'profile': 'Profile'
      };
      setAuthFeature(featureNames[tabId as keyof typeof featureNames]);
      setShowAuthModal(true);
      return;
    }
    
    onTabChange(tabId);
    setFocusedIndex(index);
    
    // Log analytics for specific tabs
    if (tabId === 'trends') {
      logAction('trends_tab_click', { previous_tab: activeTab });
    } else if (tabId === 'discover') {
      logAction('discover_tab_click', { previous_tab: activeTab });
    } else if (tabId === 'tag-teams') {
      logAction('tag_teams_tab_click', { previous_tab: activeTab });
    }
  };

  useKeyboardNavigation({
    onArrowLeft: () => {
      const newIndex = focusedIndex > 0 ? focusedIndex - 1 : tabs.length - 1;
      setFocusedIndex(newIndex);
      buttonRefs.current[newIndex]?.focus();
    },
    onArrowRight: () => {
      const newIndex = focusedIndex < tabs.length - 1 ? focusedIndex + 1 : 0;
      setFocusedIndex(newIndex);
      buttonRefs.current[newIndex]?.focus();
    },
    onEnter: () => {
      const focusedTab = tabs[focusedIndex];
      if (focusedTab) {
        handleTabClick(focusedTab.id, focusedIndex);
      }
    }
  });

  return (
    <>
      {/* Lite Mode Toggle - Above Navigation */}
      <div className="fixed bottom-16 left-0 right-0 w-full px-safe px-2 z-10">
        {/* User can see Lite Mode status in the header now */}
      </div>
      
      <nav ref={navRef} className={`bg-card border-t border-border py-2 ${isLiteMode ? '' : 'shadow-lg'}`} role="navigation" aria-label="Main navigation">
        <div className="flex justify-around px-safe" role="tablist">
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isFocused = focusedIndex === index;
            
            // Determine styling based on authentication status
            const authRequiredTabs = ['create', 'tag-teams', 'profile'];
            const isAuthRequired = authRequiredTabs.includes(tab.id);
            const isPublicTab = ['home', 'discover', 'trends'].includes(tab.id);
            
            // Style logic for anonymous users
            const getTabStyles = () => {
              if (!user) {
                if (isAuthRequired) {
                  // Gray out auth-required tabs for anonymous users but keep clickable to open login
                  return isActive
                    ? 'text-gray-400 bg-gray-100/50 cursor-pointer'
                    : 'text-gray-300 hover:text-gray-400 cursor-pointer';
                } else if (isPublicTab) {
                  // Bold/emphasize public tabs for anonymous users
                  return isActive
                    ? `text-green-800 bg-green-50 font-bold ${isLiteMode ? '' : 'scale-105'}`
                    : `text-gray-700 hover:text-green-700 font-bold ${isLiteMode ? '' : 'hover:bg-gray-50'}`;
                }
              }
              
              // Default authenticated user styles
              return isActive
                ? `text-green-800 bg-green-50 ${isLiteMode ? '' : 'scale-105'}`
                : `text-gray-500 hover:text-green-700 ${isLiteMode ? '' : 'hover:bg-gray-50'}`;
            };
            
            return (
              <button
                key={tab.id}
                ref={el => buttonRefs.current[index] = el}
                onClick={() => handleTabClick(tab.id, index)}
                onFocus={() => setFocusedIndex(index)}
                
                className={`flex flex-col items-center p-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isLiteMode 
                    ? 'transition-colors duration-100' 
                    : 'transition-all duration-200'
                } ${getTabStyles()} ${isFocused ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                role="tab"
                aria-selected={isActive}
                aria-label={`Navigate to ${tab.label} section${!user && isAuthRequired ? ' (requires login)' : ''}`}
                tabIndex={isActive ? 0 : -1}
              >
                <Icon 
                  size={18} 
                  aria-hidden="true"
                  strokeWidth={!user && isPublicTab ? 2.5 : 2}
                />
                <span className={`text-xs mt-0.5 ${!user && isPublicTab ? 'font-bold' : 'font-medium'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
        
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          feature={authFeature}
          onBrowseAnonymously={() => setShowAuthModal(false)}
        />
      </nav>
    </>
  );
};
